// apps/provider/src/services/api/supervision.js
// Provider supervision relationship methods
//
// Methods: getSupervisionRelationships, getSupervisionRelationship,
//          createSupervisionRelationship, updateSupervisionRelationship,
//          deleteSupervisionRelationship
//
// Response types: @prism-ehr/shared → ProviderSupervisionResponse

/**
 * @returns {Object} Supervision domain methods (bound to `this` of the composed apiService)
 */
export function createSupervisionMethods() {
  return {
    /**
     * Get supervision relationships with filters.
     *
     * Xano: GET /provider-supervision
     *
     * @param {Object} [filters]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.supervisor_id]
     * @param {string} [filters.supervisee_id]
     * @param {boolean} [filters.is_active]
     * @returns {Promise<import('@prism-ehr/shared').ProviderSupervisionResponse[]>}
     */
    async getSupervisionRelationships(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.supervisor_id) queryParams.append('supervisor_id', filters.supervisor_id);
      if (filters.supervisee_id) queryParams.append('supervisee_id', filters.supervisee_id);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);

      return this.request(`/provider-supervision?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single supervision relationship by ID.
     *
     * Xano: GET /provider-supervision/{supervision_id}
     *
     * @param {string} supervisionId
     * @returns {Promise<import('@prism-ehr/shared').ProviderSupervisionResponse>}
     */
    async getSupervisionRelationship(supervisionId) {
      return this.request(`/provider-supervision/${supervisionId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new supervision relationship.
     *
     * Xano: POST /provider-supervision
     *
     * @param {Object} supervisionData
     * @returns {Promise<import('@prism-ehr/shared').ProviderSupervisionResponse>}
     */
    async createSupervisionRelationship(supervisionData) {
      return this.request('/provider-supervision', {
        method: 'POST',
        body: JSON.stringify(supervisionData)
      });
    },

    /**
     * Update a supervision relationship.
     *
     * Xano: PATCH /provider-supervision/{supervision_id}
     *
     * @param {string} supervisionId
     * @param {Object} supervisionData
     * @returns {Promise<import('@prism-ehr/shared').ProviderSupervisionResponse>}
     */
    async updateSupervisionRelationship(supervisionId, supervisionData) {
      return this.request(`/provider-supervision/${supervisionId}`, {
        method: 'PATCH',
        body: JSON.stringify(supervisionData)
      });
    },

    /**
     * Delete a supervision relationship.
     *
     * Xano: DELETE /provider-supervision/{supervision_id}
     *
     * @param {string} supervisionId
     * @returns {Promise<null>}
     */
    async deleteSupervisionRelationship(supervisionId) {
      return this.request(`/provider-supervision/${supervisionId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Batch-create supervision relationships.
     *
     * Xano: POST /provider-supervision-batch
     * Xano file: provider_supervision_batch_POST.xs
     *
     * @param {Object[]} items - Array of supervision relationship objects
     * @returns {Promise<Object[]>}
     */
    async createSupervisionBatch(items) {
      return this.request('/provider-supervision-batch', {
        method: 'POST',
        body: JSON.stringify({ items })
      });
    },
  };
}
